/**
 * useTestClock — SOLE network owner for subscriptions/admin/test-clock/* routes
 * (S11 · C4, the "Relógio" tab of the staff SubscriptionDetailModal).
 *
 * Two calls:
 *   fetchState(id)  — GET  subscriptions/admin/test-clock/{id}
 *   advance(id,days) — POST subscriptions/admin/test-clock/{id}/advance
 *
 * REFUSALS ARRIVE AS HTTP 200. sendError() never touches the HTTP status
 * (MY_Controller.php), so httpService does not throw and validateResponse()
 * does not even run — the ENVELOPE's `success:false` is the only refusal
 * signal, exactly as in useSubscriptionAdmin's cancelSubscription/extend.
 * Named refusals: 'test_clock_real_gateway', 'subscription_not_found', plus
 * validation errors on `days` (positive-only, capped at 3650).
 *
 * `advances` and `offsetDaysTotal` are populated by fetchState — the audit
 * trail (quem, quando, quantos dias, offset acumulado). `advance()` also
 * updates `offsetDaysTotal` and `lastChronology` directly from its own
 * response so the tab has something to show before a re-fetch, but it does
 * NOT append to `advances` itself: the history list is the server's own
 * record, never assembled client-side.
 *
 * Module-scope singleton, like every other composable in this domain.
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_TestClockStateResponseDados } from '../contracts/Subscriptions_TestClockStateResponse'
import type { Subscriptions_TestClockAdvanceInput } from '../contracts/Subscriptions_TestClockAdvanceInput'
import type { Subscriptions_TestClockAdvanceResponseDados } from '../contracts/Subscriptions_TestClockAdvanceResponse'
import type { Subscriptions_TestClockAdvanceRow } from '../contracts/Subscriptions_TestClockAdvanceRow'
import type { Subscriptions_TestClockChronologyRow } from '../contracts/Subscriptions_TestClockChronologyRow'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/Subscriptions_TestClockStateResponse'
import '../contracts/Subscriptions_TestClockAdvanceInput'
import '../contracts/Subscriptions_TestClockAdvanceResponse'
import '../contracts/Subscriptions_TestClockAdvanceRow'
import '../contracts/Subscriptions_TestClockChronologyRow'

// ── Module-scope singleton state ────────────────────────────────────────────

const offsetDaysTotal = ref(0)
const advances = ref<Subscriptions_TestClockAdvanceRow[]>([])
const lastChronology = ref<Subscriptions_TestClockChronologyRow[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useTestClock() {
  /**
   * fetchState — GET subscriptions/admin/test-clock/{subscription_id}.
   * Populates offsetDaysTotal + advances (newest first, as the backend sends
   * them) and derives lastChronology from the newest advance, if any.
   */
  async function fetchState(subscriptionId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const raw = await httpService.get(
        sistemaUrl(`subscriptions/admin/test-clock/${subscriptionId}`),
        {
          responseContract: 'Subscriptions_TestClockStateResponse',
          skipErrorDisplay: false,
          throwOnEnvelopeFailure: 'test_clock_state_failed',
        },
      )
      const resp = raw as { dados: Subscriptions_TestClockStateResponseDados }
      offsetDaysTotal.value = resp.dados?.offset_days_total ?? 0
      advances.value = resp.dados?.advances ?? []
      lastChronology.value = advances.value[0]?.chronology ?? []
      log('useTestClock', 'fetchState', 'Estado do relógio carregado', {
        subscriptionId,
        offsetDaysTotal: offsetDaysTotal.value,
        advanceCount: advances.value.length,
      })
    } catch (err) {
      const reason = (err as Error)?.message ?? ''
      error.value = reason
      log('useTestClock', 'fetchState', 'Erro ao carregar estado do relógio', {
        subscriptionId,
        message: reason,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * advance — POST subscriptions/admin/test-clock/{subscription_id}/advance
   * {days}. Permanent and cumulative — there is no rollback (spec §non-goals).
   */
  async function advance(subscriptionId: number, days: number): Promise<Subscriptions_TestClockAdvanceResponseDados> {
    error.value = null
    const body: Subscriptions_TestClockAdvanceInput = { days }
    try {
      const raw = await httpService.post(
        sistemaUrl(`subscriptions/admin/test-clock/${subscriptionId}/advance`),
        body,
        {
          inputContract: 'Subscriptions_TestClockAdvanceInput',
          responseContract: 'Subscriptions_TestClockAdvanceResponse',
          skipErrorDisplay: false,
          throwOnEnvelopeFailure: 'test_clock_advance_failed',
        },
      )
      const resp = raw as { dados: Subscriptions_TestClockAdvanceResponseDados }
      offsetDaysTotal.value = resp.dados?.offset_days_total ?? offsetDaysTotal.value
      lastChronology.value = resp.dados?.chronology ?? []
      log('useTestClock', 'advance', 'Relógio avançado', {
        subscriptionId,
        days,
        offsetDaysTotal: offsetDaysTotal.value,
      })
      return resp.dados
    } catch (err) {
      const reason = (err as Error)?.message ?? ''
      error.value = reason
      log('useTestClock', 'advance', 'Avanço não concluído', {
        subscriptionId,
        days,
        message: reason,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    offsetDaysTotal.value = 0
    advances.value = []
    lastChronology.value = []
    loading.value = false
    error.value = null
  }

  return {
    offsetDaysTotal,
    advances,
    lastChronology,
    loading,
    error,
    fetchState,
    advance,
    _reset,
  }
}
